import * as React from 'react';
import { FormControl } from 'react-bootstrap';

import type { OwnProps as PlainDropdownProps, PropsWithLabel, PropsWithCustomRender, DropdownOptionType } from 'af-components/Controls/Dropdown';
import PlainDropdown from 'af-components/Controls/Dropdown';

import styles from './styles.module.scss';
import type { CellError } from '../../error.types';

type FieldValue = string | number | undefined;

/**
 * RHFFieldProps are props coming from field in react-hook-form
 */
interface RHFFieldProps {
	name: string;
	value: FieldValue;
	onChange: React.ChangeEventHandler;
	onBlur: () => void;
	onFocus: () => void;
}

type Props<T extends DropdownOptionType> = RHFFieldProps & PlainDropdownProps<T> & {
	elementKey: string;
	placeholder?: string;
	className?: string;
	error?: CellError;
	bold?: boolean;
};

const MANDATORY_SECTION_PROPS_KEYS = ['sections', 'sectionTitleKey', 'sectionOptionsKey'];

function DropdownCell<T extends DropdownOptionType>(props): React.ReactElement<Props<T>> {

	const {
		elementKey,
		value,
		onChange,
		className,
		defaultValue,
		handleFocus,
		error,
		valueKey,
		labelKey,
		useSectionList,
		options,
		sectionOptionsKey,
		sectionTitleKey,
		sections,
		renderSectionHeader,
		autoOpen,
		bold,
	} = props;

	// QA id is automatically generated to help with automated tests
	// We want to avoid accidentally forgetting to place an id on an element, making it harder for QA to write automated tests, so we keep it generated
	// Styling should never come from id, anyway
	const id = React.useMemo(() => (
		`accqa__dropdown__${elementKey}`
	), [elementKey]);

	const getClassName = React.useMemo(() => {
		let cn = className ?? `${styles['dropdown-cell']}`;

		if (error) {
			cn = `${cn} ${styles['error-wrapper']}`;
			if (error.type === 'required') {
				cn = `${cn} ${styles['error-placeholder']}`;
			}
		}
		if (bold) {
			cn = `${cn} ${styles.bold}`;
		}
		return cn;
	}, [bold, className, error]);

	/**
	 * Get Dropdown props that cannot coexist.
	 *
	 * If the state of the props is invalid, will log an error or warning to the console.
	 */
	const getRenderExclusiveProps = React.useCallback(() => {
		if ('renderMenuItem' in props && 'labelKey' in props) {
			// eslint-disable-next-line no-console
			console.warn('[Dropdown Field]: `renderMenuItem` and `labelKey` are mutually exclusive, please use just one of them');

			const { renderMenuItem, renderSelected } = props as PropsWithCustomRender<T> & PropsWithLabel<T>;
			return { renderMenuItem, renderSelected, labelKey };
		}
		if ('renderMenuItem' in props) {
			const { renderMenuItem, renderSelected } = props;
			return { renderMenuItem, renderSelected };
		}
		if ('labelKey' in props) {
			return { labelKey };
		}
		// eslint-disable-next-line no-console
		throw new Error('[Dropdown Field]: Cannot render without `renderMenuItem` or `labelKey` defined');
	}, [labelKey, props]);

	const getMenuListExclusiveProps = React.useCallback(() => {
		if ('useSectionList' in props && 'options' in props) {
			// eslint-disable-next-line no-console
			console.warn('[Dropdown Field]: `sections` and `options` are mutually exclusive, please use just one of them');
		}
		if ('useSectionList' in props) {
			if (!MANDATORY_SECTION_PROPS_KEYS.every((_key) => _key in props)) {
				// eslint-disable-next-line no-console
				throw new Error(`[Dropdown Field]: is missing one of the following fields ${MANDATORY_SECTION_PROPS_KEYS}`);
			}
			return {
				renderSectionHeader,
				sectionOptionsKey,
				sections,
				sectionTitleKey,
				useSectionList,
			};
		}
		if ('options' in props) {
			if (MANDATORY_SECTION_PROPS_KEYS.some((_key) => _key in props)) {
				// eslint-disable-next-line no-console
				throw new Error('[Dropdown Field]: Some of section props like `sections`, `sectionTitleKey`, `sectionOptionsKey`, `renderSectionHeader` are used with `options`, please remove them');
			}
			return { options };
		}
		// eslint-disable-next-line no-console
		throw new Error('[Dropdown Field]: Cannot render without `options` or `sections` defined');
	}, [options, props, renderSectionHeader, sectionOptionsKey, sectionTitleKey, sections, useSectionList]);

	const selectedOption = React.useMemo(() => {
		if (!value) {
			return null;
		}
		const _selectedOption = options.find((option) => option[valueKey] === value);
		return _selectedOption;
	}, [options, value, valueKey]);

	const onFocusOnCell = React.useCallback(() => {
		if (!!handleFocus) {
			handleFocus();
		}
	}, [handleFocus]);

	const onValueChanged = React.useCallback((_value) => {
		onChange?.(_value?.[valueKey] ?? null);
	}, [onChange, valueKey]);

	const renderCell = React.useMemo(() => (
		<>
			<PlainDropdown<T>
				{...props}
				containerClassName={getClassName}
				defaultValue={selectedOption ?? defaultValue}
				id={id}
				isWhite={error ? false : true}
				key={elementKey}
				onMenuOpen={onFocusOnCell}
				onValueChange={onValueChanged}
				withBorder={false}
				{...getRenderExclusiveProps()}
				{...getMenuListExclusiveProps()}
				fixed={true}
				{...(autoOpen ? { autoOpen: true } : {})}
			/>
			<FormControl.Feedback />
		</>
	), [
		props,
		getClassName,
		selectedOption,
		defaultValue,
		id,
		error,
		elementKey,
		onFocusOnCell,
		onValueChanged,
		getRenderExclusiveProps,
		getMenuListExclusiveProps,
		autoOpen,
	]);

	return renderCell;
}

export default React.memo(DropdownCell) as typeof DropdownCell;
